'use client'
import React, { useState, type JSX } from 'react'
import classNames from 'classnames'
import { ExpandButton } from '../expand-button'
import { useTranslations } from '../hooks'
import { innerText } from '../utils/inner-text'

const filterBtnElements = (jsx: JSX.Element[] | JSX.Element | string) => {
  if (typeof jsx === 'string') {
    return jsx
  }
  if (!Array.isArray(jsx)) {
    return jsx.type !== 'button' && jsx
  }

  const elements = jsx.flat() as JSX.Element[]
  return elements.filter(element => element.type !== 'button')
}

export type collapseColorTheme = 'gray' | 'white'

type Props = {
  heightLimit?: number
  characterLimit?: number
  colorTheme?: collapseColorTheme
  text?: string
  open?: boolean
  children?: string | JSX.Element | JSX.Element[]
}

const Collapse = ({
  colorTheme = 'gray',
  heightLimit = 100,
  characterLimit = 450,
  text,
  open = false,
  children,
}: Props) => {
  const { translate } = useTranslations()
  const [contentReadMoreOpen, setContentReadMoreOpen] = useState(open)

  const elements = children && filterBtnElements(children)

  if (!elements && !text) {
    console.error('Collapse: no children or text prop is provided')
    return
  }

  if (elements && text) {
    console.error('Collapse: children and text props are provided simultaneously')
    return
  }

  const limiter = elements
    ? innerText(elements).length > characterLimit
    : text!.length > characterLimit

  return (
    <div>
      <div
        tabIndex={0}
        onFocus={() => {
          limiter && setContentReadMoreOpen(true)
        }}
        onBlur={() => {
          limiter && setContentReadMoreOpen(false)
        }}
        id="content"
        className={classNames('c-collapse u-overflow-hidden', {
          'c-collapse--expand': contentReadMoreOpen || !limiter,
          'c-collapse--gray': colorTheme === 'gray' && !contentReadMoreOpen && limiter,
          'c-collapse--white': colorTheme === 'white' && !contentReadMoreOpen && limiter,
        })}
        style={{
          height: contentReadMoreOpen ? undefined : `${limiter ? heightLimit : undefined}px`,
          maxHeight: contentReadMoreOpen ? '1600px' : `${limiter ? heightLimit : undefined}px`,
          marginBottom: limiter || contentReadMoreOpen ? '8px' : '24px',
        }}>
        {text && (
          <div
            className="col-12 col-md-11 col-lg-10 u-px-0 p-sm"
            dangerouslySetInnerHTML={{ __html: text }}></div>
        )}
        {elements && <div className="col-12 u-px-0 p-sm">{elements}</div>}
      </div>
      {limiter && (
        <ExpandButton
          collapsedTitle={translate('ReadMore')}
          expandedTitle={translate('Hide')}
          expanded={contentReadMoreOpen}
          onClick={() => setContentReadMoreOpen(!contentReadMoreOpen)}
          ariaControls="content"
          className="u-mb-24"
        />
      )}
    </div>
  )
}

export default Collapse
